import log from 'loglevel';
import { ExclusionRule } from '@/API';
import { ExclusionRuleService } from '@/service/ExclusionRuleService';
import { ListType } from '@/service/ExportJobService';

/**
 * Service that caches exclusion rules to ensure they're always available when components need them.
 */
export class ExclusionCacheService {
  private exclusionCache = new Map<ListType, ExclusionRule[]>();
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(private exclusionRuleService: ExclusionRuleService) {}

  /**
   * Initialize the cache by loading all exclusions for all list types
   */
  async initialize(): Promise<void> {
    // Prevent multiple simultaneous initialization attempts
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.isInitialized) {
      return;
    }

    this.initializationPromise = this.performInitialization();
    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async performInitialization(): Promise<void> {
    const startTime = performance.now();

    try {
      // Single database call to fetch all exclusions
      const result = await this.exclusionRuleService.search({});

      // Group exclusions by list type in memory (dynamically based on actual data)
      const exclusionsByType = new Map<ListType, ExclusionRule[]>();

      // Group the results by list type
      result.items.forEach(exclusion => {
        if (exclusion.listType) {
          const listType = exclusion.listType as ListType;
          const existing = exclusionsByType.get(listType) || [];
          existing.push(exclusion);
          exclusionsByType.set(listType, existing);
        }
      });

      // Store in cache
      exclusionsByType.forEach((exclusions, listType) => {
        this.exclusionCache.set(listType, exclusions);
      });

      this.isInitialized = true;

      const endTime = performance.now();
      log.debug(
        `Exclusion cache initialized: ${exclusionsByType.size} list types, ${result.items.length} total rules in ${(endTime - startTime).toFixed(2)}ms`
      );
    } catch (error) {
      log.error('Failed to initialize exclusion cache:', error);
      // Initialize with empty cache rather than throwing to prevent app crashes
      this.isInitialized = true;
      throw error; // Re-throw to let caller handle UI feedback
    }
  }

  /**
   * Get exclusions for a specific list type (synchronous access only since cache should be pre-loaded)
   */
  getExclusions(listType: ListType): ExclusionRule[] {
    if (!this.isInitialized) {
      log.warn(
        `Exclusion cache not initialized, returning empty array for ${listType}`
      );
      return [];
    }
    return this.exclusionCache.get(listType) || [];
  }

  /**
   * Check if the cache has been initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}
